import type { NumberOfItems } from './valueObjects/numberOfItems';
import type { PhotoId } from './valueObjects/photoId';
import type { PhotoItemId } from './valueObjects/photoItemId';
import type { PhotoItemName } from './valueObjects/photoItemName';
import type { Price } from './valueObjects/price';
import type { S3Filename } from './valueObjects/s3Filename';
import type { PhotoAppOrderJson } from '@/types/domain/photo/entities/photoAppOrderEntity';

import { EntityPvt } from '@/domain/entityPvt';
import { EntityName } from '@/infra/appsync';
import { EntityNamePvt } from '@/types/common/entityPvt';

/**
 * 写真販売プロパティ
 */
export type PhotoAppOrderProps = {
  /** 写真ID */
  photoId: PhotoId,
  /** S3ファイル名 */
  s3Filename: S3Filename | undefined,
  /** 写真アイテムID */
  photoItemId: PhotoItemId,
  /** 写真アイテム名 */
  photoItemName: PhotoItemName,
  /** 価格 */
  price: Price,
  /** アイテム数 */
  numberOfItems: NumberOfItems,
}

/**
 * 注文した写真Entity.
 */
export class PhotoAppOrderEntity extends EntityPvt<
  PhotoAppOrderProps,
  PhotoAppOrderJson
> {
  toJsonData(): PhotoAppOrderJson {
    return {
      photoId: this.photoId.value,
      s3Filename: this.s3Filename ? this.s3Filename.value : undefined,
      photoItemId: this.photoItemId.value,
      photoItemName: this.photoItemName.value,
      price: this.price.value,
      numberOfItems: this.numberOfItems.value,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityNamePvt {
    const name: EntityNamePvt = EntityNamePvt.WagakoNurseryPhotoPhotoAppEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return this.photoId.value;
  }

  /**
   * 写真ID
   */
  get photoId(): PhotoId {
    return this._value.photoId;
  }

  /**
   * 写真ID
   */
  set photoId(photoId: PhotoId) {
    this._value.photoId = photoId;
  }

  /** S3ファイル名 */
  get s3Filename(): S3Filename | undefined {
    return this._value.s3Filename;
  }

  /** S3フォルダパス */
  get s3FolderPath(): string {
    const entityName = EntityName.WagakoNurseryPhotoPhotoEntity;
    return `${entityName}/${this.photoId}/`;
  }

  /**
   * 写真アイテムID
   */
  get photoItemId(): PhotoItemId {
    return this._value.photoItemId;
  }

  /**
   * 写真アイテム名
   */
  get photoItemName(): PhotoItemName {
    return this._value.photoItemName;
  }

  /**
   * 価格
   */
  get price(): Price {
    return this._value.price;
  }

  /**
   * アイテム数
   */
  get numberOfItems(): NumberOfItems {
    return this._value.numberOfItems;
  }

  /**
   * S3ファイルパス取得
   * @returns パス
   */
  getS3PathOriginal() {
    if (this.s3Filename) {
      return `${this.s3FolderPath}${this.s3Filename.value}`;
    }
    return undefined;
  }

  /**
   * サムネイルのS3ファイルパス取得
   * @returns パス
   */
  getS3PathThumbnail() {
    return `${this.s3FolderPath}thumbnail.webp`;
  }

  /**
   * サンプルのS3ファイルパス取得
   * @returns パス
   */
  getS3PathSample() {
    return `${this.s3FolderPath}sample.webp`;
  }
}
